import { RELATIONSHIP_TO_PERSON } from 'lib/probate/estate-questionnaire-schema'

// mirrors server/src/shared/config/enums.ts RELATIONSHIP_TO_PERSON
type RelationshipOption = {
  value: string
  label: string
  /** Kept for records that already have it, but not offered for new ones. */
  legacy?: true
}

const RELATIONSHIP_OPTIONS: RelationshipOption[] = [
  { value: RELATIONSHIP_TO_PERSON.HUSBAND, label: 'Husband' },
  { value: RELATIONSHIP_TO_PERSON.WIFE, label: 'Wife' },
  { value: RELATIONSHIP_TO_PERSON.CIVIL_PARTNER, label: 'Civil partner' },
  {
    value: RELATIONSHIP_TO_PERSON.PARTNER_UNMARRIED,
    label: 'Partner (unmarried)',
  },
  { value: RELATIONSHIP_TO_PERSON.FATHER, label: 'Father' },
  { value: RELATIONSHIP_TO_PERSON.MOTHER, label: 'Mother' },
  { value: RELATIONSHIP_TO_PERSON.BIOLOGICAL_CHILD, label: 'Biological child' },
  { value: RELATIONSHIP_TO_PERSON.ADOPTED_CHILD, label: 'Adopted child' },
  { value: RELATIONSHIP_TO_PERSON.STEP_CHILD, label: 'Step child' },
  { value: RELATIONSHIP_TO_PERSON.GRANDCHILD, label: 'Grandchild' },
  // Replaced by the child types above, which Exizent needs to tell apart.
  { value: RELATIONSHIP_TO_PERSON.CHILD, label: 'Child', legacy: true },
  { value: RELATIONSHIP_TO_PERSON.DAUGHTER, label: 'Daughter', legacy: true },
  { value: RELATIONSHIP_TO_PERSON.SON, label: 'Son', legacy: true },
  { value: RELATIONSHIP_TO_PERSON.GRANDMOTHER, label: 'Grandmother' },
  { value: RELATIONSHIP_TO_PERSON.GRANDFATHER, label: 'Grandfather' },
  { value: RELATIONSHIP_TO_PERSON.AUNT, label: 'Aunt' },
  { value: RELATIONSHIP_TO_PERSON.UNCLE, label: 'Uncle' },
  { value: RELATIONSHIP_TO_PERSON.SIBLING, label: 'Sibling' },
  { value: RELATIONSHIP_TO_PERSON.SISTER, label: 'Sister' },
  { value: RELATIONSHIP_TO_PERSON.BROTHER, label: 'Brother' },
  { value: RELATIONSHIP_TO_PERSON.NIECE, label: 'Niece' },
  { value: RELATIONSHIP_TO_PERSON.NEPHEW, label: 'Nephew' },
  { value: RELATIONSHIP_TO_PERSON.COUSIN, label: 'Cousin' },
  { value: RELATIONSHIP_TO_PERSON.OTHER_FAMILY, label: 'Other family' },
  { value: RELATIONSHIP_TO_PERSON.FRIEND, label: 'Friend' },
  { value: RELATIONSHIP_TO_PERSON.CLIENT, label: 'Client' },
  { value: RELATIONSHIP_TO_PERSON.OTHER, label: 'Other' },
]

/**
 * What a person is to the deceased. A legacy option is only offered to the
 * record that already has it, so nothing saved goes blank.
 */
export const relationshipOptions = (currentValue?: unknown) =>
  RELATIONSHIP_OPTIONS.filter(
    ({ value, legacy }) => !legacy || value === currentValue
  )

/** How a relationship reads, e.g. biological_child as Biological child. */
export const relationshipLabel = (value: string) =>
  RELATIONSHIP_OPTIONS.find((option) => option.value === value)?.label ?? value

export const RELATIONSHIP_LABEL = 'This is the deceased’s…'

export const OWNERSHIP_OPTIONS = [
  { label: 'Solely owned', value: 'sole' },
  { label: 'Jointly owned', value: 'joint' },
]

export const YES_NO_DONT_KNOW_OPTIONS = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
  { label: 'Don’t know', value: 'unsure' },
]

// mirrors server/src/shared/config/enums.ts PROPERTY_OWNERSHIP
export const JOINT_OWNERSHIP_TYPE_OPTIONS = [
  { label: 'Joint tenants', value: 'joint_tenants' },
  { label: 'Tenants in common', value: 'tenants_in_common' },
  { label: 'Don’t know', value: 'unsure' },
]

export const TENURE_OPTIONS = [
  { label: 'Leasehold', value: 'leasehold' },
  { label: 'Freehold', value: 'freehold' },
  { label: 'Don’t know', value: 'unsure' },
]

// mirrors server/src/shared/api/quotes/probate/interfaces.ts PROPERTY_TYPE
export const PROPERTY_TYPE_OPTIONS = [
  { label: 'Second or holiday home', value: 'second_home' },
  { label: 'Rental property', value: 'rental' },
  { label: 'Overseas property', value: 'overseas' },
  { label: 'Other', value: 'other' },
]

// mirrors server/src/shared/api/quotes/probate/interfaces.ts VEHICLE_TYPE
export const VEHICLE_TYPE_OPTIONS = [
  { label: 'Car', value: 'car' },
  { label: 'Motorbike', value: 'motorbike' },
  { label: 'Van', value: 'van' },
  { label: 'Caravan or motorhome', value: 'caravan_or_motorhome' },
  { label: 'Boat', value: 'boat' },
  { label: 'Other', value: 'other' },
]

// mirrors server/src/shared/api/quotes/probate/interfaces.ts VEHICLE_CONDITION
export const VEHICLE_CONDITION_OPTIONS = [
  { label: 'Excellent', value: 'excellent' },
  { label: 'Good', value: 'good' },
  { label: 'Fair', value: 'fair' },
  { label: 'Poor', value: 'poor' },
]

// mirrors server/src/shared/api/quotes/probate/interfaces.ts UTILITY_TYPE
export const UTILITY_TYPE_OPTIONS = [
  { label: 'Electricity', value: 'electricity' },
  { label: 'Gas', value: 'gas' },
  { label: 'Water', value: 'water' },
  { label: 'Broadband', value: 'broadband' },
  { label: 'Landline phone', value: 'landline_phone' },
  { label: 'Mobile phone', value: 'mobile_phone' },
  { label: 'TV licence', value: 'tv_licence' },
  { label: 'Satellite or cable TV', value: 'satellite_or_cable_tv' },
  { label: 'Other', value: 'other' },
]
